// https://babeljs.io/blog/2019/03/19/7.4.0#migration-from-core-js-2
import 'core-js/stable';
import 'regenerator-runtime/runtime';

// should be imported before other code to run first

import React from 'react';
import * as ReactDOM from 'react-dom';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Cookies from 'js-cookie';
import { loadableReady } from '@loadable/component';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

import { createIntl } from 'ha/i18n';
import { getAppServices } from 'ha/services/getAppServices';

import { AppContextProvider } from 'ha/myredux/AppContextProvider';

import { createAppStore } from 'ha/myredux/createAppStore';
import { getInitialState } from 'ha/myredux/getInitialState';

import { reportError } from 'ha/helpers/bugsnag/reportError';
import { isIgnoredHosts } from 'ha/utils/isIgnoredHosts';

import { Cookies as CookiesConstants } from 'ha/constants/Cookies';
import { initSession } from 'ha/modules/AuthLogic/actions/initSession';
import { setupMediaQueryListeners } from 'ha/modules/UI/actions';
import { getUserId } from 'ha/modules/AuthLogic/selectors';
import { getLanguageFromPathname } from 'ha/utils/getLanguageFromUrl';
import { initAnalytics } from 'ha/modules/Analytics/actions';
import { newReduxTracker as newAffiliationTracker } from 'ha/modules/Affiliation';
import { handleInstallPrompt } from 'ha/serviceWorker/utils/handleInstallPrompt';
import { initServiceWorker } from 'ha/serviceWorker/utils/initServiceWorker';
import { getHaSwUrl } from 'ha/serviceWorker/utils/getHaSwUrl';
import { createAlgoliaService as algoliaService } from 'ha/services/algolia';
import { SplitIOClient } from 'ha/services/SplitIO';
import { AppRoot } from 'ha/components/AppRoot';

import { FetchClient } from 'ha/helpers/FetchClient';
import { track } from '@hbf/analytics';
import { GlobalState } from 'ha/types/store';
import { Token } from '@ha/auth/browser';
import { config } from './setup';

import { createRoutes } from './createRoutes';

if (global.Bugsnag) {
  global.Bugsnag.app.version = `${GIT_COMMIT}`;
}

/* eslint-disable no-underscore-dangle */
const preloadedState = (global.__PRELOADED_STATE__ as GlobalState) || {};
/* eslint-enable no-underscore-dangle */

const rootEl = document.getElementById('app-root');

const lang = getLanguageFromPathname(window.location.pathname);
const intl = createIntl({
  lang,
  // no need
  // translation are passed through babel-plugin-translations during build time
  messages: {},
  baseDomain: config.baseDomain,
  rmsBaseDomain: config.rms.baseDomain,
});

global.baseUrl = config.baseDomain;

const fetchOptions = {
  headers: {
    'X-HA-Lang': lang,
  },
};

let authorizationHeader: (() => string | undefined) | undefined;

if (
  preloadedState?.FEATURE_FLAGS?.enableOAuthTokamak === 'on' &&
  config.authEnabled
) {
  const token = Token.getNew();
  token.load();

  authorizationHeader = () => token.authorizationHeaderValue;
}

const haApi = new FetchClient(
  config.backend.host,
  fetchOptions,
  authorizationHeader,
);

const initSplitClient = (userKey: string) =>
  import(/* webpackChunkName: "splitio" */ '@splitsoftware/splitio').then(
    ({ SplitFactory }) =>
      SplitFactory({
        ...config.split,
        core: {
          ...config.split.core,
          key: userKey,
        },
      }).client(),
  );

const getAlgoliaClient = () =>
  import(/* webpackChunkName: "algoliasearch" */ 'ha/helpers/algoliaWrapper')
    .then(module => module.algolia)
    .then(algoliaSDK =>
      algoliaSDK(config.algolia.appId, config.algolia.apiKey),
    );

const algolia = algoliaService({
  initializer: getAlgoliaClient,
  index: config.algolia.index,
});

const services = getAppServices({
  config,
  clients: {
    algolia,
    haApi,
    osiris: new FetchClient(config.osiris.host, undefined, authorizationHeader),
    metis: new FetchClient(config.metis.host, {
      credentials: 'omit',
    }),
    hermes: new FetchClient(config.hermes.host, {
      credentials: 'omit',
    }),
    almanac: new FetchClient(config.almanac.host, {
      credentials: 'omit',
    }),
    bell: new FetchClient(config.bell.host, undefined, authorizationHeader),
    cookie: Cookies,
    splitio: new SplitIOClient({
      initializer: initSplitClient,
      isClient: true,
    }),
  },
  lang,
});

// we need to pass router to store in order to use URLLocationHandler middleware
// but router also needs store to be created so we pass router as a ref object and initialize it later
const routerRef: { current: ReturnType<typeof createBrowserRouter> | null } = {
  current: null,
};

const store = createAppStore({
  config,
  errorTracker: global.Bugsnag,
  initialState: getInitialState({
    preloadedState,
    language: lang,
  }),
  services,
  routerRef,
});

window.addEventListener(
  'beforeinstallprompt',
  handleInstallPrompt({
    onChoice: choiceResult =>
      track('Web App Install Banner', { action: choiceResult.outcome }),
    onError: reportError,
  }),
);

Object.defineProperty(global, 'reduxStore', {
  get: () => store,
  set: () => {
    throw new Error('Trying to modify `window.reduxStore` property!');
  },
});

Promise.all([
  new Promise((resolve: any) => loadableReady(resolve)),
  store.dispatch(initSession(lang)),
])
  .catch(reportError)
  .then(() => {
    const userId = getUserId(store.getState());

    if (global.Bugsnag && userId) {
      global.Bugsnag.user = {
        id: userId,
      };
    }
  })
  .then(
    () =>
      new Promise(resolve => {
        if (isIgnoredHosts(global.location.host)) {
          // @ts-ignore
          resolve();
        } else {
          const router = createBrowserRouter(createRoutes(store, intl, config));

          routerRef.current = router;

          const cache = createCache({ key: 'css', prepend: true });

          ReactDOM.hydrate(
            // @ts-ignore
            <CacheProvider value={cache}>
              <AppContextProvider
                config={config}
                intl={intl}
                store={store}
                services={services}
              >
                <AppRoot>
                  <RouterProvider router={router} />
                </AppRoot>
              </AppContextProvider>
            </CacheProvider>,
            rootEl,
            resolve,
          );
        }
      }),
  )
  .then(() => {
    store.dispatch(setupMediaQueryListeners());
  })
  .then(() => {
    if ('serviceWorker' in global.navigator && config.webApp.enableSW) {
      initServiceWorker(
        getHaSwUrl(
          config.baseDomain,
          Cookies.getJSON(CookiesConstants.PWA_NEXT_ENABLED) as number,
        ),
        {
          origin: global.location.origin,
          onError: reportError,
        },
      );
    }
  })
  .then(() => {
    newAffiliationTracker(services.cookie).trackSource(
      new URL(window.location.href),
    );
    store.dispatch(
      initAnalytics({
        config,
        url: new URL(window.location.href),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        referrer: (global as any).document.referrer,
        setCookie: services.cookie.setCookie,
      }),
    );
  })
  .catch(reportError)
  .then(() => {
    global.appReady = true;
  });
